import { PaneNode, TabState } from '../../types/pane.types';
import { SplitPane as SplitPaneType, TabContainer as TabContainerType } from '../../types/pane.types';
import logger from './logger';

// ヘルパー関数: ノード内でコンテナを検索
function findContainerInNode(node: PaneNode, containerId: string): boolean {
  if (node.type === 'container') {
    return node.id === containerId;
  } else if (node.type === 'split') {
    return findContainerInNode(node.leftChild, containerId) || 
           findContainerInNode(node.rightChild, containerId);
  }
  return false;
}

/**
 * アクティブなコンテナを見つけるヘルパー関数
 * アクティブコンテナとは、最後にユーザーが操作したペインのこと
 * 
 * @param rootPane - ルートペインノード
 * @param activeContainerId - 現在のアクティブコンテナID
 * @returns アクティブコンテナのID、または見つからない場合はnull
 */
export function findActiveContainer(
  rootPane: PaneNode | null,
  activeContainerId: string | null
): string | null {
  if (!rootPane) return null;
  
  // activeContainerIdが設定されている場合、実際に存在するかチェック
  if (activeContainerId) {
    const containerExists = findContainerInNode(rootPane, activeContainerId);
    if (containerExists) {
      return activeContainerId;
    }
    // 存在しない場合はフォールバック
    logger.warn(`[paneUtils] Active container ID '${activeContainerId}' not found in layout`);
  }
  
  // フォールバック: 最初のコンテナを返す
  const findFirstContainer = (node: PaneNode): string | null => {
    if (node.type === 'container') {
      return node.id;
    } else {
      // Split pane の場合は、まず右側を、次に左側を優先
      const rightResult = findFirstContainer(node.rightChild);
      if (rightResult) return rightResult;
      
      const leftResult = findFirstContainer(node.leftChild);
      if (leftResult) return leftResult;
      
      return null;
    }
  };
  
  return findFirstContainer(rootPane);
}

/**
 * 兄弟ペインを見つけるヘルパー関数
 * 兄弟ペインとは、同じ親（SplitPane）を持つもう一方のペインのこと
 * 
 * @param rootPane - ルートペインノード
 * @param containerId - 対象コンテナのID
 * @returns 兄弟ペインのID、または見つからない場合はnull
 */
export function findSiblingPane(
  rootPane: PaneNode | null,
  containerId: string
): string | null {
  if (!rootPane) return null;
  
  
  const findInNode = (node: PaneNode, targetId: string): { parent: SplitPaneType | null, isLeft: boolean } | null => {
    if (node.type === 'split') {
      if (node.leftChild.type === 'container' && node.leftChild.id === targetId) {
        return { parent: node, isLeft: true };
      }
      if (node.rightChild.type === 'container' && node.rightChild.id === targetId) {
        return { parent: node, isLeft: false };
      }
      
      // 再帰的に探索
      const leftResult = findInNode(node.leftChild, targetId);
      if (leftResult) return leftResult;
      
      const rightResult = findInNode(node.rightChild, targetId);
      if (rightResult) return rightResult;
    }
    return null;
  };
  
  const result = findInNode(rootPane, containerId);
  
  if (result && result.parent) {
    // 兄弟ペインを返す
    const sibling = result.isLeft ? result.parent.rightChild : result.parent.leftChild;
    
    if (sibling.type === 'container') {
      return sibling.id;
    }
  }
  
  return null;
}

/**
 * アクティブなタブを探すヘルパー関数
 * 
 * @param node - ペインノード
 * @param activeContainerId - アクティブコンテナID
 * @returns アクティブなタブ、または見つからない場合はnull
 */
export function findActiveTab(
  node: PaneNode,
  activeContainerId: string
): TabState | null {
  if (node.type === 'container') {
    // アクティブなコンテナの場合、アクティブなタブを返す
    if (node.id === activeContainerId && node.activeTabId) {
      return node.tabs.find(tab => tab.id === node.activeTabId) || null;
    }
    return null;
  } else {
    // 分割ペインの場合、左右の子を再帰的に探索
    const leftResult = findActiveTab(node.leftChild, activeContainerId);
    if (leftResult) return leftResult;
    return findActiveTab(node.rightChild, activeContainerId);
  }
}

/**
 * 特定のタブを含むコンテナを探すヘルパー関数
 * 
 * @param node - ペインノード
 * @param tabId - 探すタブのID
 * @returns コンテナID、または見つからない場合はnull
 */
export function findContainerForTab(
  node: PaneNode,
  tabId: string
): string | null {
  if (node.type === 'container') {
    const hasTab = node.tabs.some(tab => tab.id === tabId);
    return hasTab ? node.id : null;
  } else {
    const leftResult = findContainerForTab(node.leftChild, tabId);
    if (leftResult) return leftResult;
    return findContainerForTab(node.rightChild, tabId);
  }
}

/**
 * 最初のタブを探すヘルパー関数
 * 
 * @param node - ペインノード
 * @returns 最初のタブの情報、または見つからない場合はnull
 */
export function findFirstTab(
  node: PaneNode
): { containerId: string; tabId: string } | null {
  if (node.type === 'container' && node.tabs.length > 0) {
    return { containerId: node.id, tabId: node.tabs[0].id };
  } else if (node.type === 'split') {
    return findFirstTab(node.leftChild) || findFirstTab(node.rightChild);
  }
  return null;
}

/**
 * 特定のコンテナノードを探すヘルパー関数
 * 
 * @param node - ペインノード
 * @param containerId - 探すコンテナのID
 * @returns コンテナノード、または見つからない場合はnull
 */
export function findContainerNode(
  node: PaneNode,
  containerId: string
): TabContainerType | null {
  if (node.type === 'container' && node.id === containerId) {
    return node;
  } else if (node.type === 'split') {
    return findContainerNode(node.leftChild, containerId) || 
           findContainerNode(node.rightChild, containerId);
  }
  return null;
}

/**
 * ペインツリーを走査して特定の条件を満たすノードを探す汎用関数
 *
 * @param node - ペインノード
 * @param predicate - ノードを評価する関数
 * @returns 条件を満たすノード、または見つからない場合はnull
 */
export function findNode<T extends PaneNode>(
  node: PaneNode,
  predicate: (node: PaneNode) => node is T
): T | null {
  if (predicate(node)) {
    return node;
  }

  if (node.type === 'split') {
    const leftResult = findNode(node.leftChild, predicate);
    if (leftResult) return leftResult;
    return findNode(node.rightChild, predicate);
  }

  return null;
}

/**
 * コンテナ内で次のタブのIDを取得（循環）
 *
 * @param container - タブコンテナ
 * @param currentTabId - 現在のタブID
 * @returns 次のタブのID、タブが1つ以下の場合はnull
 */
export function getNextTabInContainer(
  container: TabContainerType,
  currentTabId: string
): string | null {
  if (container.tabs.length <= 1) return null;

  const currentIndex = container.tabs.findIndex(tab => tab.id === currentTabId);
  if (currentIndex === -1) return null;

  // 次のタブへ（最後のタブの場合は最初に戻る）
  const nextIndex = (currentIndex + 1) % container.tabs.length;
  return container.tabs[nextIndex].id;
}

/**
 * コンテナ内で前のタブのIDを取得（循環）
 *
 * @param container - タブコンテナ
 * @param currentTabId - 現在のタブID
 * @returns 前のタブのID、タブが1つ以下の場合はnull
 */
export function getPreviousTabInContainer(
  container: TabContainerType,
  currentTabId: string
): string | null {
  if (container.tabs.length <= 1) return null;

  const currentIndex = container.tabs.findIndex(tab => tab.id === currentTabId);
  if (currentIndex === -1) return null;

  // 前のタブへ（最初のタブの場合は最後に移動）
  const previousIndex = (currentIndex - 1 + container.tabs.length) % container.tabs.length;
  return container.tabs[previousIndex].id;
}